import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StandardTable } from '../../../../shared/components/standard-table/standard-table/standard-table';
import { TicketsService } from '../../../../core/services/tickets.service';
import { TranslationService } from '../../../../core/services/translation.service';
import { AlertService } from '../../../../shared/components/alert/alert';
import * as XLSX from 'xlsx';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-admin-table',
  standalone: true,
  imports: [CommonModule, StandardTable, FormsModule, TranslatePipe],
  templateUrl: './admin-table.html',
  styleUrls: ['./admin-table.css'],
})
export class AdminTable implements OnInit {
  columnList = [
    { name: 'column.ticketNo', key: 'code', sortable: false },
    { name: 'column.alphabet', key: 'alphabet', sortable: false },
    { name: 'column.number', key: 'number', sortable: false },
    { name: 'column.status', key: 'status', sortable: false },
    { name: 'column.action', key: 'actions', sortable: false },
  ];

  tableDataFilters: any[] = [];
  totalItems = 0;
  isLoading = false;
  isSaving = false;
  isModalOpen = false;
  isEditMode = false;
  editingId: string | null = null;
  
  newTicket = {
    alphabet: 'A-',
    number: ''
  };

  ticketRows: { alphabet: string; number: string }[] = [
    { alphabet: 'A-', number: '' }
  ];

  currentPage = 1;
  pageSize = 10;
  searchQuery = '';
  selectedYear = '';
  selectedMonth = '';
  selectedStatus = '';
  selectedAlphabet = '';

  constructor(
    private ticketsService: TicketsService, 
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    const now = new Date();
    this.selectedYear = now.getFullYear().toString();
    this.selectedMonth = (now.getMonth() + 1).toString().padStart(2, '0');
    this.loadTickets();
  }

  loadTickets() {
    this.isLoading = true;
    
    
    let dateParam = '';
    if (this.selectedYear && this.selectedMonth) {
      dateParam = `${this.selectedYear}-${this.selectedMonth}`;
    }

    const payload = {
      page: this.currentPage.toString(),
      limit: this.pageSize.toString(),
      search: this.searchQuery,
      date: dateParam,
      status: this.selectedStatus,
      alphabet: this.selectedAlphabet
    };

    this.ticketsService.getAllTickets(payload).subscribe({
      next: (res: any) => {
        if (res.returncode === '300') {
          this.tableDataFilters = res.data.data.map((t: any) => ({
            ...t,
            code: t.alphabet + t.number,
          }));
          this.totalItems = res.data.meta.totalItems || res.data.meta.total || 0;
        } else {
          this.tableDataFilters = [];
          this.totalItems = 0;
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch tickets in admin table:', err);
        this.tableDataFilters = [];
        this.totalItems = 0;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  search(value: string) {
    this.searchQuery = value;
    this.currentPage = 1;
    this.loadTickets();
  }

  onDateFilterChange(filter: {year: string, month: string}) {
    this.selectedYear = filter.year;
    this.selectedMonth = filter.month;
    this.currentPage = 1;
    this.loadTickets();
  }

  onStatusFilterChange(status: string) {
    this.selectedStatus = status;
    this.currentPage = 1;
    this.loadTickets();
  }

  onAlphabetFilterChange(alphabet: string) {
    this.selectedAlphabet = alphabet;
    this.currentPage = 1;
    this.loadTickets();
  }

  onSort(column: any) {
    console.log('Sort:', column);
  }

  onPageChange(page: any) {
    this.currentPage = page;
    this.loadTickets();
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadTickets();
  }

  onClickRow(row: any) {
    console.log('Row clicked:', row);
  }

  openCreateModal() {
    this.isEditMode = false;
    this.editingId = null;
    this.isModalOpen = true;
    this.newTicket = { alphabet: 'A-', number: '' };
    this.ticketRows = [{ alphabet: 'A-', number: '' }];
  }

  addTicketRow() {
    this.ticketRows.push({ alphabet: 'A-', number: '' });
  }

  removeTicketRow(index: number) {
    if (this.ticketRows.length > 1) {
      this.ticketRows.splice(index, 1);
    }
  }

  isFormValid(): boolean {
    if (this.isEditMode) {
      return !!this.newTicket.number.trim();
    }
    return this.ticketRows.length > 0 && this.ticketRows.every(r => !!r.number.trim());
  }

  onEditTicket(row: any) {
    this.isEditMode = true;
    this.editingId = row.id;
    this.isModalOpen = true;
    this.newTicket = { 
        alphabet: row.alphabet.includes('-') ? row.alphabet : row.alphabet + '-', 
        number: row.number 
    };
  }

  onDeleteTicket(row: any) {
    if (confirm(this.translationService.translate('alert.deleteTicketConfirm') + ` ${row.alphabet}${row.number}?`)) {
      this.ticketsService.deleteTicket(row.id).subscribe({
        next: (res: any) => {
          if (res.returncode === '300') {
            this.alertService.success(this.translationService.translate('alert.deleteSuccess'));
            this.loadTickets();
          } else {
            this.alertService.error(res.message || this.translationService.translate('alert.deleteError'));
          }
        },
        error: (err) => {
          console.error('Error deleting ticket:', err);
          this.alertService.error(this.translationService.translate('alert.errorOccurred'));
        }
      });
    }
  }

  closeModal() {
    this.isModalOpen = false;
  }

  submitTicket() {
    if (!this.isFormValid()) return;

    this.isSaving = true;

    if (this.isEditMode) {
      this.ticketsService.updateTicket({ ...this.newTicket, id: this.editingId }).subscribe({
        next: (res: any) => {
          this.isSaving = false;
          if (res.returncode === '300') {
            this.alertService.success(this.translationService.translate('alert.updateSuccess'));
            this.closeModal();
            this.loadTickets();
          } else {
            this.alertService.error(res.message || this.translationService.translate('alert.updateError'));
          }
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error updating ticket:', err);
          this.alertService.error(this.translationService.translate('alert.errorOccurred'));
        }
      });
    } else {
      const payload = this.ticketRows.filter(r => r.number.trim());
      this.ticketsService.createMultipleTickets(payload).subscribe({
        next: (res: any) => {
          this.isSaving = false;
          if (res.returncode === '300') {
            this.alertService.success(this.translationService.translate('alert.createSuccess'));
            this.closeModal();
            this.loadTickets();
          } else {
            this.alertService.error(res.message || this.translationService.translate('alert.createError'));
          }
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error creating tickets:', err);
          this.alertService.error(this.translationService.translate('alert.errorOccurred'));
        }
      });
    }
  }

  async importTickets(data: any[]) {
    
    if (!data || data.length === 0) {
      this.alertService.error('No data found in Excel file');
      return;
    }

    this.isSaving = true;
    this.isLoading = true;

    const ticketsToCreate = data.map((item: any) => {
      const keys = Object.keys(item);
      const alphabetKey = keys.find(k => k.toLowerCase().trim() === 'alphabet');
      const numberKey = keys.find(k => ['number', 'code', 'ticket number', 'ticketno'].includes(k.toLowerCase().trim()));

      const alphabet = alphabetKey ? String(item[alphabetKey]).trim() : '';
      const number = numberKey ? String(item[numberKey]).trim() : '';
      
      return { alphabet, number };
    }).filter(t => t.number);


    if (ticketsToCreate.length === 0) {
      console.warn('Column names found in first row:', Object.keys(data[0] || {}));
      this.alertService.error(this.translationService.translate('alert.noValidTicketData'));
      this.isSaving = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    try {
      this.ticketsService.createMultipleTickets(ticketsToCreate).subscribe({
        next: (res: any) => {
          if (res.returncode === '300') {
            this.alertService.success(this.translationService.translate('alert.importSuccess', { count: ticketsToCreate.length }));
            this.loadTickets();
          } else {
            this.alertService.error(res.message || this.translationService.translate('alert.importError'));
          }
          this.finishImport();
        },
        error: (err) => {
          console.error('Bulk import error:', err);
          this.alertService.error(this.translationService.translate('alert.errorOccurred'));
          this.finishImport();
        }
      });
    } catch (error) {
      console.error('Import process error:', error);
      this.alertService.error(this.translationService.translate('alert.errorOccurred'));
      this.finishImport();
    }
  }

  private finishImport() {
    this.isSaving = false;
    this.isLoading = false;
    this.cdr.detectChanges();
  }
}
